import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import ProgressBar from '../components/ProgressBar';
import API from '../services/api';
import { IconFolder, IconCamera, IconCalendar, IconHardHat, IconShovel, IconFoundation, IconBrickWall, IconScaffold, IconTruss, IconRoof, IconWrench, IconTrowel, IconTile, IconPaintBrush, IconDoor, IconDroplet, IconSofa } from '../components/Icons';
import { formatDate, formatShortDate, formatTime, formatDateTime, inputDateOrDefault, isDateOverdue } from '../utils/dateUtils';

const getMilestoneIcon = (stageName, size = 15) => {
  const n = stageName.toLowerCase();
  if (n.includes('excavat')) return <IconShovel size={size} />;
  if (n.includes('plinth') || n.includes('pcc') || n.includes('plain cement')) return <IconFoundation size={size} />;
  if (n.includes('brick')) return <IconBrickWall size={size} />;
  if (n.includes('center') || n.includes('shutter')) return <IconScaffold size={size} />;
  if (n.includes('fabricat') || n.includes('truss') || n.includes('steel')) return <IconTruss size={size} />;
  if (n.includes('roof')) return <IconRoof size={size} />;
  if (n.includes('plumb') || n.includes('electr')) return <IconWrench size={size} />;
  if (n.includes('plaster')) return <IconTrowel size={size} />;
  if (n.includes('tile') || n.includes('granite') || n.includes('floor')) return <IconTile size={size} />;
  if (n.includes('paint') || n.includes('carpet')) return <IconPaintBrush size={size} />;
  if (n.includes('window') || n.includes('door')) return <IconDoor size={size} />;
  if (n.includes('water')) return <IconDroplet size={size} />;
  if (n.includes('furnish') || n.includes('handover')) return <IconSofa size={size} />;
  return <IconHardHat size={size} />;
};

const STATUS_OPTIONS = ['not_started', 'active', 'completed'];
const STATUS_LABELS = { not_started: 'Not Started', active: 'Active', completed: 'Completed', delayed: 'Delayed' };
const STATUS_COLORS = { completed: '#009966', active: '#f59e0b', delayed: '#ef4444', not_started: '#9ca3af' };

function BungalowDetail({ projectCode }) {
  const { unitNumber } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [plot, setPlot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [savingAll, setSavingAll] = useState(false);
  const [savedSuccess, setSavedSuccess] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [uploadError, setUploadError] = useState('');

  // Local pending changes per stage (not yet saved)
  const [pendingChanges, setPendingChanges] = useState({});
  // Comment text per stage
  const [stageNotes, setStageNotes] = useState({});
  // Which stage's comment input is visible
  const [showCommentFor, setShowCommentFor] = useState(null);
  // Which stage's comment is being posted
  const [postingComment, setPostingComment] = useState(null);
  // Target date editing
  const [editingTarget, setEditingTarget] = useState(false);
  const [targetDate, setTargetDate] = useState('');
  const [savingTarget, setSavingTarget] = useState(false);
  // Stage-level photo upload
  const [uploadingStagePhoto, setUploadingStagePhoto] = useState(null);
  // Photo preview before upload
  const [photoPreview, setPhotoPreview] = useState(null); // { files, stage, previewUrls }
  // Lightbox
  const [lightboxImg, setLightboxImg] = useState(null);

  const fetchPlot = async () => {
    try {
      const res = await API.get(`/bungalows/slug/${projectCode}/${unitNumber}`);
      setPlot(res.data.plot);
    } catch (err) {
      setError('Failed to load bungalow details.');
    }
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError('');
      await fetchPlot();
      setLoading(false);
    };
    load();
  }, [projectCode, unitNumber]);

  useEffect(() => {
    const stageId = searchParams.get('stage');
    if (!stageId || loading) return;
    const el = document.getElementById(`stage-${stageId}`);
    if (el) {
      setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'center' }), 100);
    }
  }, [loading, searchParams]);

  const handleStageStatusChange = (stage, newStatus) => {
    const currentCompletion = pendingChanges[stage.stage_id]?.completion_percentage ?? stage.completion_percentage;
    const completion = newStatus === 'completed' ? 100
      : newStatus === 'not_started' ? 0
      : newStatus === 'active' ? 20
      : currentCompletion;
    setPendingChanges(prev => ({
      ...prev,
      [stage.stage_id]: { status: newStatus, completion_percentage: completion }
    }));
  };

  const handleCompletionChange = (stage, percentage) => {
    const newStatus = percentage === 100 ? 'completed' : percentage > 0 ? 'active' : 'not_started';
    setPendingChanges(prev => ({
      ...prev,
      [stage.stage_id]: { status: newStatus, completion_percentage: percentage }
    }));
  };

  const handleSaveAll = async () => {
    const stageIds = Object.keys(pendingChanges);
    if (stageIds.length === 0) return;
    setSavingAll(true);
    try {
      for (const stageId of stageIds) {
        const pending = pendingChanges[stageId];
        const stage = plot.stages.find(s => s.stage_id === parseInt(stageId));
        if (!stage) continue;
        await API.put(`/bungalows/${plot.id}/stage-progress`, {
          stage_id: parseInt(stageId),
          status: pending.status,
          completion_percentage: pending.completion_percentage,
          notes: stage.notes || null
        });
      }
      setPendingChanges({});
      await fetchPlot();
      setSavedSuccess(true);
      setTimeout(() => setSavedSuccess(false), 3000);
    } catch (err) {
      alert('Failed to save changes. Please try again.');
    } finally {
      setSavingAll(false);
    }
  };

  const handlePostComment = async (stage) => {
    const comment = stageNotes[stage.stage_id] || '';
    if (!comment.trim()) return;
    setPostingComment(stage.stage_id);
    try {
      await API.put(`/bungalows/${plot.id}/stage-progress`, {
        stage_id: stage.stage_id,
        status: stage.status,
        completion_percentage: stage.completion_percentage,
        notes: comment,
        comment_only: true
      });
      setStageNotes(prev => { const n = { ...prev }; delete n[stage.stage_id]; return n; });
      setShowCommentFor(null);
      await fetchPlot();
    } catch (err) {
      alert('Failed to post comment. Please try again.');
    } finally {
      setPostingComment(null);
    }
  };

  const handleSaveTargetDate = async () => {
    if (!targetDate) return;
    setSavingTarget(true);
    try {
      await API.put(`/bungalows/${plot.id}/target-date`, { target_date: targetDate });
      setEditingTarget(false);
      await fetchPlot();
    } catch (err) {
      alert('Failed to update target date.');
    } finally {
      setSavingTarget(false);
    }
  };

  const formatTargetDate = formatDate;

  const handleSidebarPhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    e.target.value = '';
    if (files.length === 0) return;
    const previewUrls = files.map(f => URL.createObjectURL(f));
    setPhotoPreview({ files, stage: null, previewUrls });
  };

  const handleStagePhotoUpload = (e, stage) => {
    const files = Array.from(e.target.files); // convert before clearing input
    e.target.value = '';
    if (files.length === 0) return;
    const previewUrls = files.map(f => URL.createObjectURL(f));
    setPhotoPreview({ files, stage, previewUrls });
  };

  const handleCancelPhotoPreview = () => {
    if (photoPreview) {
      photoPreview.previewUrls.forEach(url => URL.revokeObjectURL(url));
      setPhotoPreview(null);
    }
  };

  const handleConfirmPhotos = async () => {
    if (!photoPreview) return;
    const { files, stage, previewUrls } = photoPreview;
    setPhotoPreview(null);
    previewUrls.forEach(url => URL.revokeObjectURL(url));
    const formData = new FormData();
    files.forEach(f => formData.append('images', f));
    if (stage) {
      setUploadingStagePhoto(stage.stage_id);
      formData.append('stage_id', stage.stage_id);
    } else {
      setUploadingImages(true);
      setUploadError('');
    }
    try {
      await API.post(`/images/bungalow/${plot.id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      await fetchPlot();
    } catch (err) {
      if (stage) alert('Photo upload failed. Please try again.');
      else setUploadError('Image upload failed. Please try again.');
    } finally {
      if (stage) setUploadingStagePhoto(null);
      else setUploadingImages(false);
    }
  };

  const backPath = plot?.project_code === 'SKY' ? '/projects/skyora' : '/projects/sylvan-villas';
  const getStageColor = (status) => STATUS_COLORS[status] || STATUS_COLORS.not_started;

  return (
    <div className="page">
      <Navbar />
      <main className="main-content">
        <button className="back-btn" onClick={() => navigate(backPath || '/dashboard')}>
          ← Back to Project
        </button>

        {loading && <div className="loading-spinner">Loading bungalow details...</div>}
        {error && <div className="error-message">{error}</div>}

        {plot && (
          <div className="plot-detail-layout">
            <div className="plot-detail-main">
              <div className="plot-detail-header">
                {/* Top row: badge + title + overall progress */}
                <div className="pdh-top">
                  <div className="pdh-title-block">
                    <div className="pdh-plot-badge">
                      <span className="pdh-badge-label">{plot.project_code === 'SKY' ? 'U. No.' : 'B. No.'}</span>
                      <span className="pdh-badge-number">{plot.bungalow_number.replace(/\D/g, '')}</span>
                    </div>
                    <div className="pdh-title-area">
                      <div className="pdh-title-row">
                        <h1 className="plot-detail-title">
                          {plot.project_code === 'SKY'
                            ? plot.bungalow_number.replace('U. No.', 'Unit No.')
                            : plot.bungalow_number.replace('B. No.', 'Bungalow No.')} Overview
                        </h1>
                        <span className="plot-detail-status-badge" style={{ backgroundColor: getStageColor(plot.status) + '22', color: getStageColor(plot.status), border: `1.5px solid ${getStageColor(plot.status)}` }}>
                          {STATUS_LABELS[plot.status] || 'Not Started'}
                        </span>
                      </div>
                      <div className="pdh-meta">
                        <span className="pdh-meta-item"><IconFolder size={13} /> {plot.project_name}</span>
                        <span className="pdh-meta-item">
                          <IconCalendar size={13} />
                          {!editingTarget ? (
                            <>
                              <span className="pdh-target-label">Target:</span>
                              <span className="pdh-target-date">{plot.target_completion_date ? formatTargetDate(plot.target_completion_date) : 'Not set'}</span>
                              <button className="change-target-btn" onClick={() => { setEditingTarget(true); setTargetDate(inputDateOrDefault(plot.target_completion_date)); }}>
                                {plot.target_completion_date ? 'Change' : 'Set Target'}
                              </button>
                              {plot.target_completion_date && plot.status !== 'completed' && isDateOverdue(plot.target_completion_date) && (
                                <span className="project-delayed-badge">⚠ Delayed</span>
                              )}
                            </>
                          ) : (
                            <span className="target-input-row">
                              <input type="date" className="target-date-input" value={targetDate} onChange={e => setTargetDate(e.target.value)} />
                              <button className="target-save-btn" onClick={handleSaveTargetDate} disabled={savingTarget}>{savingTarget ? '...' : 'Save'}</button>
                              <button className="target-cancel-btn" onClick={() => setEditingTarget(false)}>Cancel</button>
                            </span>
                          )}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="pdh-overall">
                    <div className="pdh-overall-label">Overall Construction</div>
                    <div className="pdh-overall-percent">{plot.overall_completion || 0}%</div>
                    <ProgressBar percentage={plot.overall_completion || 0} height="8px" />
                    {plot.current_stage_name && plot.status !== 'completed' && (
                      <div className="pdh-current-stage">Active: {plot.current_stage_name}</div>
                    )}
                  </div>
                </div>

                {/* Milestone horizontal timeline */}
                {plot.stages && plot.stages.length > 0 && (
                  <>
                    <div className="pdh-divider"></div>
                    <div className="pdh-milestone-label">Milestone Completion Status</div>
                    <div className="pdh-milestone-scroll">
                      <div className="milestone-timeline">
                        {plot.stages.map((stage, idx) => {
                          const displayStatus = pendingChanges[stage.stage_id]?.status ?? stage.status;
                          const displayCompletion = pendingChanges[stage.stage_id]?.completion_percentage ?? stage.completion_percentage;
                          const isCompleted = displayStatus === 'completed';
                          const isActive = displayStatus === 'active';
                          const isDelayed = displayStatus === 'delayed';
                          const statusKey = isCompleted ? 'done' : isActive ? 'active' : isDelayed ? 'delayed' : 'pending';
                          const isFirst = idx === 0;
                          const isLast = idx === plot.stages.length - 1;
                          const prevStage = idx > 0 ? plot.stages[idx - 1] : null;
                          const prevStatus = prevStage ? (pendingChanges[prevStage.stage_id]?.status ?? prevStage.status) : null;
                          const leftLineDone = prevStatus === 'completed';
                          return (
                            <div key={stage.stage_id} className="ms-step">
                              <div className={`ms-icon ms-icon-${statusKey}`}>
                                {getMilestoneIcon(stage.stage_name, 16)}
                              </div>
                              <div className="ms-track">
                                {isFirst
                                  ? <div className="ms-line-invisible"></div>
                                  : <div className={`ms-line ${leftLineDone ? 'ms-line-done' : 'ms-line-pending'}`}></div>
                                }
                                <div className={`ms-circle ms-circle-${statusKey}`}>
                                  {isCompleted && (
                                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                                      <polyline points="20 6 9 17 4 12" />
                                    </svg>
                                  )}
                                </div>
                                {isLast
                                  ? <div className="ms-line-invisible"></div>
                                  : <div className={`ms-line ${isCompleted ? 'ms-line-done' : 'ms-line-pending'}`}></div>
                                }
                              </div>
                              <div className={`ms-label ${isCompleted ? 'ms-label-done' : isActive ? 'ms-label-active' : isDelayed ? 'ms-label-delayed' : ''}`}>
                                {stage.stage_name}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </>
                )}
              </div>

              <div className="stages-section">
                <div className="stages-section-header">
                  <div>
                    <h2 className="section-title">Sequential Milestone Timeline</h2>
                    <p className="section-subtitle">Update status and completion for each milestone, then click Save to apply changes.</p>
                  </div>
                  <div className="stages-save-bar">
                    {Object.keys(pendingChanges).length > 0 && (
                      <span className="stages-unsaved-indicator">● {Object.keys(pendingChanges).length} unsaved {Object.keys(pendingChanges).length === 1 ? 'change' : 'changes'}</span>
                    )}
                    {savedSuccess && <span className="stages-saved-indicator">✓ Saved</span>}
                    <button
                      className="stages-save-btn"
                      onClick={handleSaveAll}
                      disabled={savingAll || Object.keys(pendingChanges).length === 0}
                    >
                      {savingAll ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                </div>

                {plot.stages && plot.stages.map((stage, idx) => {
                  const pending = pendingChanges[stage.stage_id];
                  const displayStatus = pending?.status ?? stage.status;
                  const displayCompletion = pending?.completion_percentage ?? stage.completion_percentage;
                  const hasPending = !!pending;

                  return (
                    <div
                      key={stage.stage_id}
                      id={`stage-${stage.stage_id}`}
                      className={`stage-timeline-item ${displayStatus === 'completed' ? 'stage-done' : displayStatus === 'active' ? 'stage-active' : displayStatus === 'delayed' ? 'stage-delayed' : ''}`}
                    >
                      <div className="stage-timeline-connector">
                        <div
                          className="stage-timeline-circle"
                          style={{ backgroundColor: getStageColor(displayStatus) }}
                        >
                          {displayStatus === 'completed' ? '✓' : idx + 1}
                        </div>
                        {idx < plot.stages.length - 1 && <div className="stage-timeline-line"></div>}
                      </div>

                      <div className="stage-timeline-content">
                        <div className="stage-timeline-header">
                          <div>
                            <h3 className="stage-timeline-name">{stage.stage_name}</h3>
                            {stage.stage_description && (
                              <p className="stage-timeline-desc">{stage.stage_description}</p>
                            )}
                          </div>
                          <span
                            className="stage-status-badge"
                            style={{ backgroundColor: getStageColor(displayStatus) + '22', color: getStageColor(displayStatus), border: `1px solid ${getStageColor(displayStatus)}` }}
                          >
                            {STATUS_LABELS[displayStatus] || 'Not Started'}
                          </span>
                        </div>

                        <div className="stage-status-controls">
                          <span className="stage-controls-label">STATUS CONTROLS:</span>
                          <div className="stage-status-btns">
                            {STATUS_OPTIONS.map((s) => (
                              <button
                                key={s}
                                className={`stage-status-btn ${displayStatus === s ? 'active' : ''}`}
                                style={displayStatus === s ? { backgroundColor: getStageColor(s), color: '#fff', borderColor: getStageColor(s) } : {}}
                                onClick={() => handleStageStatusChange(stage, s)}
                                disabled={savingAll}
                              >
                                {STATUS_LABELS[s]}
                              </button>
                            ))}
                          </div>
                        </div>

                        <div className="stage-progress-row">
                          <span className="stage-progress-label">Completion progress for this milestone</span>
                          <span className="stage-progress-pct">{displayCompletion}%</span>
                        </div>
                        <div className="stage-progress-btns">
                          {[20, 40, 60, 80, 100].map(pct => (
                            <button
                              key={pct}
                              className={`stage-progress-btn ${displayCompletion === pct ? 'active' : ''}`}
                              onClick={() => handleCompletionChange(stage, pct)}
                              disabled={savingAll}
                            >
                              {pct}%
                            </button>
                          ))}
                        </div>

                        {/* Per-stage comment logs */}
                        {(() => {
                          const stageLogs = (plot.activity_logs || []).filter(
                            l => l.stage_id === stage.stage_id && l.details
                          );
                          return stageLogs.length > 0 ? (
                            <div className="stage-log-notes">
                              <div className="stage-log-notes-title">
                                STAGE LOG NOTES ({stageLogs.length})
                              </div>
                              <div className="stage-log-notes-list">
                                {stageLogs.map(log => (
                                  <div key={log.id} className="stage-log-note-item">
                                    <div className="stage-log-note-meta">
                                      <span className="stage-log-note-author">{log.admin_name || 'Admin'}</span>
                                      <span className="stage-log-note-time">
                                        {formatShortDate(log.created_at)} {formatTime(log.created_at)}
                                      </span>
                                    </div>
                                    <div className="stage-log-note-text">{log.details}</div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ) : null;
                        })()}

                        <div className="stage-action-btns">
                          <button
                            className="add-comment-btn"
                            onClick={() => setShowCommentFor(showCommentFor === stage.stage_id ? null : stage.stage_id)}
                          >
                            + Add Comment
                          </button>
                          <label className="add-photos-btn">
                            {uploadingStagePhoto === stage.stage_id ? 'Uploading...' : '+ Add Photos'}
                            <input
                              type="file"
                              multiple
                              accept="image/*"
                              style={{ display: 'none' }}
                              disabled={uploadingStagePhoto === stage.stage_id}
                              onChange={(e) => handleStagePhotoUpload(e, stage)}
                            />
                          </label>
                        </div>

                        {showCommentFor === stage.stage_id && (
                          <div className="stage-note-row">
                            <input
                              type="text"
                              className="stage-note-input"
                              placeholder="Add a comment for this stage..."
                              value={stageNotes[stage.stage_id] || ''}
                              onChange={(e) => setStageNotes(prev => ({ ...prev, [stage.stage_id]: e.target.value }))}
                              autoFocus
                              onKeyDown={(e) => e.key === 'Enter' && handlePostComment(stage)}
                            />
                            <button
                              className="stage-comment-post-btn"
                              onClick={() => handlePostComment(stage)}
                              disabled={postingComment === stage.stage_id || !stageNotes[stage.stage_id]?.trim()}
                            >
                              {postingComment === stage.stage_id ? 'Posting...' : 'Post'}
                            </button>
                            <button
                              className="stage-comment-cancel-btn"
                              onClick={() => {
                                setShowCommentFor(null);
                                setStageNotes(prev => { const n = { ...prev }; delete n[stage.stage_id]; return n; });
                              }}
                            >
                              Cancel
                            </button>
                          </div>
                        )}

                        {/* Stage photos grid */}
                        {(() => {
                          const stageImgs = (plot.images || []).filter(img => img.stage_id === stage.stage_id);
                          return stageImgs.length > 0 ? (
                            <div className="stage-photos-grid">
                              {stageImgs.map(img => (
                                <div
                                  key={img.id}
                                  className="stage-photo-thumb"
                                  onClick={() => setLightboxImg(`${process.env.REACT_APP_UPLOADS_URL}/${img.filename}`)}
                                >
                                  <img
                                    src={`${process.env.REACT_APP_UPLOADS_URL}/${img.filename}`}
                                    alt={img.caption || ''}
                                    className="stage-photo-img"
                                  />
                                </div>
                              ))}
                            </div>
                          ) : null;
                        })()}

                        {hasPending && (
                          <div className="stage-pending-indicator">● Unsaved changes</div>
                        )}
                      </div>
                    </div>
                  );
                })}

                <div className="stages-save-bar stages-save-bar--bottom">
                  {Object.keys(pendingChanges).length > 0 && (
                    <span className="stages-unsaved-indicator">● {Object.keys(pendingChanges).length} unsaved {Object.keys(pendingChanges).length === 1 ? 'change' : 'changes'}</span>
                  )}
                  {savedSuccess && <span className="stages-saved-indicator">✓ Saved</span>}
                  <button
                    className="stages-save-btn"
                    onClick={handleSaveAll}
                    disabled={savingAll || Object.keys(pendingChanges).length === 0}
                  >
                    {savingAll ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
            </div>

            <div className="plot-detail-sidebar">
              <div className="sidebar-card">
                <h3 className="sidebar-card-title">Site Photos — {plot.bungalow_number}</h3>
                {uploadError && <div className="upload-error">{uploadError}</div>}
                <label className="upload-btn">
                  {uploadingImages ? 'Uploading...' : <><IconCamera size={15} /> Upload Photos</>}
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleSidebarPhotoUpload}
                    disabled={uploadingImages}
                    style={{ display: 'none' }}
                  />
                </label>
                <div className="images-grid">
                  {plot.images && plot.images.filter(img => !img.stage_id).length > 0 ? (
                    plot.images.filter(img => !img.stage_id).map((img) => (
                      <div key={img.id} className="image-thumb" style={{ cursor: 'zoom-in' }} onClick={() => setLightboxImg(`${process.env.REACT_APP_UPLOADS_URL}/${img.filename}`)}>
                        <img
                          src={`${process.env.REACT_APP_UPLOADS_URL}/${img.filename}`}
                          alt={img.caption || img.filename}
                          className="thumb-img"
                        />
                        {img.caption && <div className="thumb-caption">{img.caption}</div>}
                      </div>
                    ))
                  ) : (
                    <div className="no-images">No photos uploaded yet.</div>
                  )}
                </div>
              </div>

              <div className="sidebar-card">
                <h3 className="sidebar-card-title">Recent Activity</h3>
                <div className="audit-log-list">
                  {plot.activity_logs && plot.activity_logs.length > 0 ? (
                    plot.activity_logs.map((log) => (
                      <div key={log.id} className="audit-log-item">
                        <div className="audit-log-action">{log.action}</div>
                        {log.details && <div className="audit-log-details">{log.details}</div>}
                        <div className="audit-log-meta">
                          <span>{formatDateTime(log.created_at)}</span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="audit-empty">No past updates logged.</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Photo preview modal */}
      {photoPreview && (
        <div className="photo-preview-overlay" onClick={handleCancelPhotoPreview}>
          <div className="photo-preview-modal" onClick={e => e.stopPropagation()}>
            <div className="photo-preview-header">
              <div>
                <h3 className="photo-preview-title">Preview Photos</h3>
                <p className="photo-preview-subtitle">
                  {photoPreview.files.length} photo{photoPreview.files.length > 1 ? 's' : ''} selected
                  &nbsp;·&nbsp; {photoPreview.stage ? photoPreview.stage.stage_name : 'Site Photos'}
                </p>
              </div>
              <button className="photo-preview-close-btn" onClick={handleCancelPhotoPreview}>✕</button>
            </div>
            <div className="photo-preview-grid">
              {photoPreview.previewUrls.map((url, idx) => (
                <div key={idx} className="photo-preview-thumb">
                  <img src={url} alt={`Preview ${idx + 1}`} />
                </div>
              ))}
            </div>
            <div className="photo-preview-actions">
              <button className="photo-preview-cancel" onClick={handleCancelPhotoPreview}>Cancel</button>
              <button className="photo-preview-confirm" onClick={handleConfirmPhotos}>Upload Photos</button>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox */}
      {lightboxImg && (
        <div className="photo-lightbox" onClick={() => setLightboxImg(null)}>
          <img src={lightboxImg} alt="Stage photo" className="photo-lightbox-img" onClick={e => e.stopPropagation()} />
          <button className="photo-lightbox-close" onClick={() => setLightboxImg(null)}>✕</button>
        </div>
      )}
    </div>
  );
}

export default BungalowDetail;
